import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, createApiClient } from '../App';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Heart,
  MapPin,
  Star,
  SpinnerGap,
  ForkKnife,
  Camera,
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FavouriteDishes = () => {
  const { token } = useAuth();
  const api = createApiClient(token);
  const [dishes, setDishes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/favourite-dishes');
        setDishes(res.data.dishes || []);
      } catch { toast.error('Failed to load dishes'); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return (
      <div className="container-app py-8 pb-8 flex justify-center pt-32">
        <SpinnerGap className="w-8 h-8 animate-spin text-[#A94438]" />
      </div>
    );
  }

  return (
    <div className="container-app py-8 pb-8" data-testid="favourite-dishes-page">
      <h1 className="text-3xl sm:text-4xl font-heading font-semibold text-[#1A1918] mb-2" data-testid="dishes-title">
        Your Greatest Hits
      </h1>
      <p className="text-[#7A7873] mb-8">All your favourite dishes in one place</p>

      {dishes.length === 0 && (
        <div className="empty-state">
          <Heart weight="duotone" className="empty-state-icon" />
          <h3 className="empty-state-title">No favourite dishes yet</h3>
          <p className="empty-state-text">Mark dishes as favourites when logging a visit</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {dishes.map((dish, idx) => {
          const photoUrl = dish.photos?.length > 0
            ? `${BACKEND_URL}/api${dish.photos[0].file_path}`
            : null;

          return (
            <motion.div
              key={`${dish.dish_name}-${dish.visit_id}-${idx}`}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.03 }}
            >
              <Link
                to={`/restaurants/${dish.restaurant_id}`}
                className="block rounded-2xl border border-[#EAE8E3] bg-white overflow-hidden hover:shadow-md transition-shadow"
                data-testid={`dish-card-${idx}`}
              >
                {photoUrl ? (
                  <div className="h-44 relative">
                    <img src={photoUrl} alt={dish.dish_name} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                    <div className="absolute top-3 right-3 bg-white/90 rounded-full p-1.5">
                      <Heart weight="fill" className="w-4 h-4 text-[#A94438]" />
                    </div>
                  </div>
                ) : (
                  <div className="h-28 bg-[#F9F8F5] flex items-center justify-center">
                    <ForkKnife weight="duotone" className="w-10 h-10 text-[#EAE8E3]" />
                  </div>
                )}
                <div className="p-4">
                  <h3 className="font-heading font-semibold text-[#1A1918] mb-1">{dish.dish_name}</h3>
                  <div className="flex items-center gap-1 text-sm text-[#7A7873] mb-1">
                    <MapPin weight="duotone" className="w-3.5 h-3.5" />
                    {dish.restaurant_name}
                  </div>
                  {dish.visited_at && (
                    <p className="text-xs text-[#9A9590]">
                      {new Date(dish.visited_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                  )}
                </div>
              </Link>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default FavouriteDishes;
